"use client";

import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import { ContactUsPageURL } from "@/lib/appConstants";
import { Loader2 } from "lucide-react";
import { useBookStore } from "@/stores/useBookStore";
import { useState } from "react";

type CustomButtonProps = {
  label: string;
  bg?: boolean;
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  enabled?: boolean;
  title?: string;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  showSpinnerOnClick?: boolean;
};

const labelToRoute: Record<string, string> = {
  "Contact Us": ContactUsPageURL,
  "Read the Documentation": "/docs",
  "See All FAQs": "/docs/general/faqs",
  "Get Started": "/checkout",
  "See Pricing": "/checkout",
  "Browse Plans": "/checkout",
  "Upgrade Now": "/checkout",
  "New Book": "/checkout",
  "Explore All Features": "/features",
  "See Live Demo": "/books", // Default fallback route for demo button
};

export default function CustomButton({ label, bg = false, icon, iconPosition = "right", enabled = true, title = "", onClick: customOnClick, className, showSpinnerOnClick = false }: CustomButtonProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const { prefetchDemoWork } = useBookStore();

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!enabled) {
      return;
    }
    if (customOnClick) {
      customOnClick(e);
      return;
    }

    if (label === "Explore Example Reports" || label === "See Live Demo") {
      e.preventDefault();
      if (showSpinnerOnClick) {
        setLoading(true);
      }
      try {
        const id = await prefetchDemoWork();
        if (id) {
          router.push(`/book/${id}`);
        } else {
          router.push("/books");
        }
      } catch (error) {
        router.push("/books");
      }
      return;
    }

    const route = labelToRoute[label];
    if (route) {
      e.preventDefault();
      if (showSpinnerOnClick) {
        setLoading(true);
      }
      router.push(route);
    }
  };

  return (
    <Button
      onClick={(e) => handleClick(e)}
      className={cn(
        "w-full group md:w-fit rounded-full py-[1.5rem] pl-[1.5rem] pr-[1rem] text-black dark:text-white font-normal text-[16px] font-poppins tracking-wider",
        bg
          ? "bg-[#A5C7D7] dark:bg-[#365665] hover:bg-[#A5C7D7] shadow-2xl"
          : "bg-transparent border border-black dark:border-white hover:bg-transparent",
        className
      )}
      disabled={!enabled}
      title={title}
    >
      {iconPosition === "left" && (loading ? <Loader2 className="animate-spin" /> : icon)}
      {label}
      <span
        className={cn(
          loading ? "ml-2" : "overflow-hidden max-w-0 opacity-0 group-hover:max-w-[30px] group-hover:mr-2 group-hover:ml-1 group-hover:opacity-100 transition-all duration-500 ease-in-out"
        )}
      >
        {iconPosition === "right" && (loading ? <Loader2 className="animate-spin" /> : icon)}
      </span>
    </Button>
  );
}
